import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { toast } from "sonner";

import { CCard, CCardBody, CCardHeader } from "@coreui/react";

import { nguyenVatLieuApi } from "src/1/apis/nguyen_vat_lieu.api";
import { getAutoSuggest } from "src/apis/purchase_proposal_form.api";
import { confirm } from "src/App";
import { ERROR_MESSAGE } from "src/configs/constant";
import { store } from "src/store";
import { isSuccess, UID } from "src/utils/funcs";
import { useMobileDevice } from "src/utils/hooks/viewport";
import { PurchaseProposalForm } from "src/utils/models/purchase-proposal-form.model";

import Dialog from "./Dialog";
import { DraftWatcher } from "./draft";
import Good from "./Good";
import Material from "./Material";
import Toolbar from "./Toolbar";

export default ({ isLoading, edit = false, data, onSubmit }) => {
  const correctQuantityRef = useRef();
  //#region Data
  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm({
    defaultValues: data,
    mode: "onSubmit",
  });

  const {
    fields: materials,
    append: appendMaterials,
    remove: removeMaterials,
    replace: replaceMaterials,
  } = useFieldArray({
    control,
    name: "materials",
    keyName: "__id",
  });

  const store_code = useWatch({ control, name: "store_code" });

  const currentMaterials = useWatch({ control, name: "materials" });

  const { data: materialsOptions } = useQuery({
    queryKey: ["danh-sach-nvl", store_code],
    queryFn: () => nguyenVatLieuApi.getMyMaterials(store_code),
    enabled: !!store_code,
    select: (response) =>
      response?.data?.data?.map((e) => ({
        ...e,
        value: e?.code,
        label: e?.name,
      })),
  });

  const [status, setStatus] = useState(1);

  const [isUpdated, setIsUpdated] = useState(false);

  const [current, setCurrentTab] = useState(false);

  const [goods, setGoods] = useState([]);

  const isMobile = useMobileDevice();

  const dispatch = useDispatch();

  const isDraftExisted = useSelector(
    (state) => state.phieuDatHangDraft.isDraftExisted,
    shallowEqual,
  );

  useEffect(() => {
    if (isDraftExisted && !edit) {
      confirm({
        title: "Có 1 bản lưu nháp!",
        content: "Bạn có muốn dùng lại bản lưu nháp trước đó không?",
        type: "warning",
        confirmText: "Dùng bản lưu nháp",
        cancelText: "Hủy bản nháp",
        onConfirm: () => {
          const _values = store.getState().phieuDatHangDraft.values;
          reset(_values);
        },
        onCancel: () => dispatch({ type: "CLEAR-DRAFT-PHIEU-DAT-HANG" }),
      });
    }
  }, [edit]);
  //#endregion

  //#region Events
  const onStatusChange = (_status) =>
    setStatus(status === _status ? 0 : _status);

  const onAutoFill = async (v) => {
    if (!v || !store_code) return;
    const res = await getAutoSuggest(store_code);

    if (isSuccess(res)) {
      replaceMaterials(
        res.map((r) => {
          const _material = materialsOptions.find((m) => m.value === r.code);
          if (_material) return { ..._material.data, ...r };
          else return r;
        }),
      );
    }
  };

  const onAdd = () => {
    current
      ? setGoods([
          ...goods,
          {
            id: UID("good_"),
            code: "",
            quantity: 0,
            boughtQ: 0,
            total: 0,
            note: "",
          },
        ])
      : appendMaterials({
          id: UID("material_"),
          code: "",
          quantity: 0,
          boughtQ: 1,
          boughtUnit: "",
          unit: "",
          total: 0,
          note: "",
        });
  };

  const onSave = () => {
    clearErrors();
    handleSubmit((d) => {
      const _materials = currentMaterials.filter((m) => m.code && m.boughtQ);
      const _goods = goods.filter((g) => g.code && g.quantity);

      if (_materials.length || _goods.length) {
        onSubmit({ ...d, materials: _materials, goods: _goods });
        dispatch({ type: "CLEAR-DRAFT-PHIEU-DAT-HANG" });
      } else {
        toast.error(ERROR_MESSAGE.NVL.REQUIRED);
        setError("materials", { error: "Required" });
      }
    })();
  };

  const onRemove = () => {
    if (current) {
      setGoods(goods.filter((g) => !g.check));
    }
    const indexList = currentMaterials.reduce((prev, cur, index) => {
      if (cur.check) prev.push(index);
      return prev;
    }, []);

    if (indexList.length === 0)
      toast.error("Vui lòng chọn nguyên vật liệu cần xóa");
    else removeMaterials(indexList);
  };

  const onCorrectQuantity = () => {
    correctQuantityRef.current.set(store_code);
  };

  const onSuccess = (v) => setIsUpdated(v);
  //#endregion

  useEffect(() => {
    if (isLoading || !data) return;
    Object.keys(data).forEach((key) => setValue(key, data[key]));
    replaceMaterials(data.materials);
    setGoods(data.goods);
  }, [isLoading, data]);

  useEffect(() => {
    const handlePress = (event) => {
      if (event.key === "Enter") {
        onAdd();
      }
    };
    window.addEventListener("keypress", handlePress);
    return () => window.removeEventListener("keypress", handlePress);
  }, []);

  //#region Render
  return (
    <>
      <CCard className={classNames("toolbar", !isMobile && "sticky")}>
        <CCardBody>
          <Toolbar
            isLoading={isSubmitting || isLoading}
            isUpdated={isUpdated}
            setIsUpdated={setIsUpdated}
            status={status}
            watch={watch}
            control={control}
            setValue={setValue}
            onStatusChange={onStatusChange}
            onAutoFill={onAutoFill}
            onAdd={onAdd}
            onSave={onSave}
            onRemove={onRemove}
            onCorrectQuantity={onCorrectQuantity}
          />
        </CCardBody>
      </CCard>
      <CCard className="mb-96">
        <CCardHeader className="p-0 tabs border-0">
          <ul className="nav nav-tabs modules">
            <li className="nav-item">
              <span
                className={classNames(
                  "nav-link text-center px-4",
                  !current && "active",
                )}
                style={{ minWidth: "200px" }}
                onClick={() => setCurrentTab(false)}
              >
                Nguyên Vật Liệu
              </span>
            </li>
            {/* <li className="nav-item">
              <span
                className={classNames(
                  "nav-link text-center px-4",
                  current && "active"
                )}
                style={{ minWidth: "200px" }}
                onClick={() => setCurrentTab(true)}
              >
                Hàng hóa
              </span>
            </li> */}
          </ul>
        </CCardHeader>
        <CCardBody className="px-0 pt-0">
          <div className="tab-content">
            <div
              className={classNames(
                "table-responsive tab-pane fade",
                !current && "show active",
              )}
            >
              <Material
                materialsOptions={materialsOptions}
                materials={materials}
                control={control}
                setValue={setValue}
              />
            </div>
            <div
              className={classNames(
                "table-responsive tab-pane fade",
                current && "show active",
              )}
            >
              <Good
                edit={edit}
                isLoading={isLoading}
                store_code={store_code}
                data={goods}
                onChange={(data) => setGoods(data)}
              />
            </div>
          </div>
        </CCardBody>
      </CCard>
      <Dialog ref={correctQuantityRef} onSuccess={onSuccess} />

      {!edit && <DraftWatcher control={control} />}
    </>
  );
  //#endregion
};

const DEFAULT_VALUES = new PurchaseProposalForm({
  code: "",
  store_code: "",
  issueDate: new Date(),
  reason: "",
  storeAddress: "",
  storePhone: "",
});

//#region Lưu nháp
const initialState = {
  isDraftExisted: false,
  values: DEFAULT_VALUES,
};

const persistConfig = {
  key: "phieu-dat-hang-draft",
  storage: storage,
};

function phieuDatHangDraftReducer(
  state = initialState,
  { type, payload } = { type: null, payload: null },
) {
  switch (type) {
    case "SAVE-DRAFT-PHIEU-DAT-HANG":
      return { isDraftExisted: true, values: payload };
    case "CLEAR-DRAFT-PHIEU-DAT-HANG":
      return { isDraftExisted: false, values: DEFAULT_VALUES };
    default:
      return state;
  }
}

export const phieuDatHangPersistReducer = persistReducer(
  persistConfig,
  phieuDatHangDraftReducer,
);
//#endregion
